import { useEffect } from 'react';
import { api } from '../lib/api';

/**
 * Analytics Injector Component
 * Fetches analytics codes from backend and injects them into the page
 * This allows admin to update tracking codes without code deployment
 */
export default function AnalyticsInjector() {
  useEffect(() => {
    const loadAndInjectAnalytics = async () => {
      try {
        const response = await api.get('/public/analytics-config');
        const config = response.data;

        // Inject Google Analytics (GA4)
        if (config.google_analytics) {
          const gaScript = document.createElement('script');
          gaScript.async = true;
          gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${config.google_analytics}`;
          document.head.appendChild(gaScript);

          const gaConfig = document.createElement('script');
          gaConfig.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${config.google_analytics}');
          `;
          document.head.appendChild(gaConfig);
          console.log('✅ Google Analytics loaded:', config.google_analytics);
        }

        // Inject Facebook Pixel
        if (config.facebook_pixel) {
          const fbScript = document.createElement('script');
          fbScript.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${config.facebook_pixel}');
            fbq('track', 'PageView');
          `;
          document.head.appendChild(fbScript);

          const fbNoscript = document.createElement('noscript');
          fbNoscript.innerHTML = `<img height="1" width="1" style="display:none"
            src="https://www.facebook.com/tr?id=${config.facebook_pixel}&ev=PageView&noscript=1" />`;
          document.body.appendChild(fbNoscript);
          console.log('✅ Facebook Pixel loaded:', config.facebook_pixel);
        }

        // Inject TikTok Pixel
        if (config.tiktok_pixel) {
          const ttScript = document.createElement('script');
          ttScript.innerHTML = `
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
              ttq.load('${config.tiktok_pixel}');
              ttq.page();
            }(window, document, 'ttq');
          `;
          document.head.appendChild(ttScript);
          console.log('✅ TikTok Pixel loaded:', config.tiktok_pixel);
        }

        // Inject Custom Header Code
        if (config.custom_header) {
          const headerDiv = document.createElement('div');
          headerDiv.innerHTML = config.custom_header;
          // Append all child nodes to head
          while (headerDiv.firstChild) {
            if (headerDiv.firstChild.nodeName === 'SCRIPT') {
              // For script tags, create new script element to ensure execution
              const script = document.createElement('script');
              if (headerDiv.firstChild.src) {
                script.src = headerDiv.firstChild.src;
                script.async = headerDiv.firstChild.async;
              } else {
                script.innerHTML = headerDiv.firstChild.innerHTML;
              }
              document.head.appendChild(script);
              headerDiv.removeChild(headerDiv.firstChild);
            } else {
              document.head.appendChild(headerDiv.firstChild);
            }
          }
          console.log('✅ Custom header code injected');
        }

        // Inject Custom Footer Code
        if (config.custom_footer) {
          const footerDiv = document.createElement('div');
          footerDiv.innerHTML = config.custom_footer;
          // Append all child nodes to body
          while (footerDiv.firstChild) {
            if (footerDiv.firstChild.nodeName === 'SCRIPT') {
              const script = document.createElement('script');
              if (footerDiv.firstChild.src) {
                script.src = footerDiv.firstChild.src;
                script.async = footerDiv.firstChild.async;
              } else {
                script.innerHTML = footerDiv.firstChild.innerHTML;
              }
              document.body.appendChild(script);
              footerDiv.removeChild(footerDiv.firstChild);
            } else {
              document.body.appendChild(footerDiv.firstChild);
            }
          }
          console.log('✅ Custom footer code injected');
        }

      } catch (error) {
        console.error('Failed to load analytics config:', error);
      }
    };

    loadAndInjectAnalytics();
  }, []);

  return null; // This component doesn't render anything
}
